/**
 * PostHog telemetry foundation (RSW-591 / Launch Hardening P0.1).
 *
 * Design decisions:
 * - Dynamically imported and initialized after first paint so telemetry never
 *   competes with LCP and stays out of the critical bundle (~250 KB).
 * - Autocapture OFF — deliberate events only (taxonomy in
 *   docs/architecture/posthog-events.md).
 * - Session replay ships with mask-everything defaults: resume content is PII.
 * - Safe no-op everywhere when no key is configured (local dev, CI, tests).
 *
 * Cloud Run injects per-environment values via `runtime-config.js`
 * (RSWORKS_POSTHOG_*); local dev can use VITE_POSTHOG_KEY / VITE_POSTHOG_HOST.
 */
const env = import.meta.env;

const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

/** posthog-js client once loaded + initialized; null until then. */
let client = null;
let initialized = false;
let starting = false;

/**
 * Exceptions and product events raised before the deferred init completes
 * (init is scheduled past first paint, but workspace_load_started and even
 * workspace_load_succeeded can fire during initial effects). Bounded buffers,
 * flushed once the client is live so early sessions keep complete funnels.
 */
const PRE_INIT_QUEUE_MAX = 20;
const PRE_INIT_EVENT_QUEUE_MAX = 50;
const preInitExceptions = [];
const preInitEvents = [];

function readRuntimePosthog() {
  if (typeof window === 'undefined') return null;
  const posthogConfig = window.__RSWORKS_RUNTIME_CONFIG__?.posthog;
  return posthogConfig && typeof posthogConfig === 'object' ? posthogConfig : null;
}

function resolveField(runtimeKey, viteKey, fallback = '') {
  const runtime = readRuntimePosthog();
  const runtimeValue = runtime?.[runtimeKey];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return String(runtimeValue);
  }
  const viteValue = env[viteKey];
  if (viteValue !== undefined && viteValue !== null && viteValue !== '') {
    return String(viteValue);
  }
  return fallback;
}

export function getPosthogConfig() {
  return {
    key: resolveField('key', 'VITE_POSTHOG_KEY'),
    host: resolveField('host', 'VITE_POSTHOG_HOST', DEFAULT_POSTHOG_HOST),
  };
}

function environmentName() {
  if (typeof window === 'undefined') return 'unknown';
  return window.__RSWORKS_RUNTIME_CONFIG__?.build?.environment || 'development';
}

function buildSha() {
  if (typeof window === 'undefined') return '';
  return window.__RSWORKS_RUNTIME_CONFIG__?.build?.sha || '';
}

function doNotTrack() {
  if (typeof navigator === 'undefined') return false;
  return navigator.doNotTrack === '1' || window.doNotTrack === '1';
}

async function startPosthog() {
  const { key, host } = getPosthogConfig();
  if (!key || initialized || starting) return;
  starting = true;

  // Dynamic import keeps posthog-js out of the critical bundle.
  const { default: posthog } = await import('posthog-js');

  posthog.init(key, {
    api_host: host,
    // Deliberate events only; the taxonomy doc is the contract.
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    // Resume content is PII: mask every input and all text by default.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    // Web vitals (LCP/INP/CLS) for the Phase 4 performance gates.
    capture_performance: { web_vitals: true },
    persistence: 'localStorage+cookie',
    advanced_disable_decide: false,
  });
  posthog.register({
    app_environment: environmentName(),
    app_build_sha: buildSha(),
  });
  client = posthog;
  initialized = true;

  // Flush anything captured before init finished (first-render errors and
  // early funnel events like workspace_load_succeeded).
  while (preInitExceptions.length > 0) {
    const { error, properties } = preInitExceptions.shift();
    captureException(error, properties);
  }
  while (preInitEvents.length > 0) {
    const { eventName, properties } = preInitEvents.shift();
    captureEvent(eventName, properties);
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      captureException(event?.error || event?.message, { source: 'window.onerror' });
    });
    window.addEventListener('unhandledrejection', (event) => {
      captureException(event?.reason, { source: 'unhandledrejection' });
    });
  }
}

/**
 * Initialize PostHog after first paint. Never blocks rendering; never throws.
 */
export function initPostHog() {
  if (typeof window === 'undefined') return;
  if (doNotTrack()) return;
  const { key } = getPosthogConfig();
  if (!key || initialized) return;

  const start = () => {
    // Telemetry must never take the app down.
    startPosthog().catch((error) => {
      console.error('PostHog init failed:', error);
    });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: 3000 });
  } else {
    window.setTimeout(start, 0);
  }
}

/** True once init has completed (exported for tests). */
export function isPosthogInitialized() {
  return initialized;
}

/** Reset module state (tests only). */
export function __resetPosthogForTests() {
  client = null;
  initialized = false;
  starting = false;
  preInitExceptions.length = 0;
  preInitEvents.length = 0;
}

/**
 * Report an exception. Safe no-op when PostHog is not initialized.
 * @param {unknown} error
 * @param {Record<string, unknown>} [properties] e.g. { source, module, componentStack }
 */
export function captureException(error, properties = {}) {
  if (!initialized || !client) {
    // A key is configured but init has not finished yet (deferred past first
    // paint): buffer so a crash during the initial render is not lost.
    const { key } = getPosthogConfig();
    if (key && preInitExceptions.length < PRE_INIT_QUEUE_MAX) {
      preInitExceptions.push({ error, properties });
    }
    return;
  }
  try {
    const err = error instanceof Error ? error : new Error(String(error ?? 'Unknown error'));
    client.captureException(err, properties);
  } catch {
    // Swallow — telemetry failures must never cascade.
  }
}

/**
 * Capture a deliberate product event (see docs/architecture/posthog-events.md).
 * Safe no-op when PostHog is not initialized.
 * @param {string} eventName
 * @param {Record<string, unknown>} [properties]
 */
export function captureEvent(eventName, properties = {}) {
  if (!initialized || !client) {
    // A key is configured but the deferred init has not finished: buffer so
    // early funnel events (workspace_load_succeeded) are not lost (RSW-593).
    const { key } = getPosthogConfig();
    if (key && preInitEvents.length < PRE_INIT_EVENT_QUEUE_MAX) {
      preInitEvents.push({ eventName, properties });
    }
    return;
  }
  try {
    client.capture(eventName, properties);
  } catch {
    // Swallow — telemetry failures must never cascade.
  }
}
